import { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { StatusPill } from "@/components/atelier/StatusPill";
import { ServerUrl } from "@/components/atelier/ServerUrl";
import { ItemActions } from "@/components/atelier/ItemActions";

const Checks = ({ checks }) => (
  <ul className="grid min-w-0 gap-1 text-xs sm:grid-cols-2" data-testid="atelier-item-checks">
    {checks.map((c) => (
      <li key={c.id} className="min-w-0 break-words" data-testid={`atelier-check-${c.id}`}>
        <span className={c.ok === false ? (c.blocking ? "font-bold text-red-300" : "text-amber-300") : c.ok ? "text-emerald-300" : "text-neutral-500"}>
          {c.ok === false ? "✕" : c.ok ? "✓" : "–"} {c.id}{c.blocking ? " *" : ""}
        </span>{" "}<span className="text-neutral-500">{c.detail}</span>
      </li>
    ))}
  </ul>
);

export const AtelierItem = ({ id, onChanged }) => {
  const { t, fmtDate } = useI18n();
  const [it, setIt] = useState(null);
  const load = useCallback(() => api.get(`/admin/atelier/items/${id}`).then(({ data }) => setIt(data)).catch((e) => toast.error(translateError(e, t))), [id, t]);
  useEffect(() => { load(); }, [load]);
  if (!it) return null;
  const done = () => { load(); onChanged(); };
  return (
    <section className="card-carbon min-w-0 space-y-4 rounded-2xl p-4" data-testid="atelier-item">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <h2 className="min-w-0 break-words font-display text-lg font-black uppercase text-white" data-testid="atelier-item-name">{it.app_name}</h2>
        <span className="text-sm text-[var(--gold-bright)]" dir="ltr" data-testid="atelier-item-version">v{it.version}{it.version_code ? ` (build ${it.version_code})` : ""}</span>
        <StatusPill status={it.status} testId="atelier-item-status" />
      </div>
      <p className="break-words text-xs text-neutral-400" dir="ltr">{it.package} · {fmtDate(it.uploaded_at)}</p>
      <ServerUrl it={it} onSaved={setIt} />
      <Checks checks={it.checks || []} />
      {it.reject_reason && <p className="break-words text-sm text-red-300" data-testid="atelier-item-reject-reason">{t("atelier.rejected")}: {it.reject_reason}</p>}
      {it.override?.reason && <p className="break-words text-xs text-amber-300" data-testid="atelier-item-override">Override: {it.override.reason} ({it.override.by})</p>}
      <div className="min-w-0">
        <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-400">{t("atelier.report")}{it.report_at ? ` · ${fmtDate(it.report_at)}` : ""}</h3>
        {it.report ? (
          <div className="atelier-md mt-2 min-w-0 rounded-xl bg-black/40 p-3" data-testid="atelier-item-report"><ReactMarkdown remarkPlugins={[remarkGfm]}>{it.report}</ReactMarkdown></div>
        ) : <p className="mt-1 text-sm text-neutral-500" data-testid="atelier-item-no-report">{t("atelier.noReport")}</p>}
      </div>
      <ItemActions it={it} onDone={done} />
    </section>
  );
};
